import React, { useMemo } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Loader2 } from "lucide-react";
import { Route, Redirect } from "wouter";

interface ProtectedRouteProps {
  path: string;
  component: React.ComponentType<Record<string, unknown>>;
}

export const ProtectedRoute = React.memo<ProtectedRouteProps>(function ProtectedRoute({
  path,
  component: Component,
}) {
  const { user, isLoading: authLoading } = useAuth();
  
  // Memoize the loading component to prevent recreation
  const loadingComponent = useMemo(() => (
    <Route path={path}>
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    </Route>
  ), [path]);

  // Memoize the redirect component to prevent recreation  
  const redirectComponent = useMemo(() => (
    <Route path={path}>
      <Redirect to="/auth" />
    </Route>
  ), [path]);

  // Memoize the authenticated route component to prevent recreation
  const authenticatedComponent = useMemo(() => (
    <Route path={path}>
      <Component />
    </Route>
  ), [path, Component]);
  
  if (authLoading) {
    console.log("[ProtectedRoute] Auth loading, showing loader. User:", user?.email);
    return loadingComponent;
  }

  // If we timed out and still don't have a user, redirect to auth
  if (!user) {
    console.log("[ProtectedRoute] No user detected (authLoading is false), redirecting to /auth");
    return redirectComponent;
  }

  console.log("[ProtectedRoute] User authenticated, rendering component");
  return authenticatedComponent;
});
